import { z } from "zod";

export const discordIdSchema = z.string().regex(/^\d{17,20}$/, "Invalid Discord ID");

export const guildIdSchema = discordIdSchema;

export const riskScoreSchema = z.number().min(0).max(100);

export const securityActionSchema = z.enum(["none", "warn", "alert", "timeout", "kick", "quarantine", "lockdown"]);

export const verificationStateSchema = z.enum(["pending", "verified", "rejected", "expired", "revoked"]);

export const guildVerificationConfigSchema = z.object({
  requireVerification: z.boolean(),
  requireStaffApproval: z.boolean(),
  expirationHours: z.number().int().min(0).max(8760),
  unverifiedRoleId: discordIdSchema.nullable(),
  verifiedRoleId: discordIdSchema.nullable(),
  maxAltScore: riskScoreSchema,
  /** VPN/proxy blocking: coming soon. */
  blockVpn: z.never().optional(),
});

/** Result of the pre-verification security screening (VPN / alt / banned checks). */
export const verificationScreeningSchema = z.object({
  vpn: z.boolean(),
  altScore: riskScoreSchema,
  banned: z.boolean(),
  reasons: z.array(z.string().max(200)).max(10),
});

/** Guild security policy payload accepted by the dashboard API and bot config commands. */
export const securityPolicySchema = z.object({
  antiRaid: z.object({
    enabled: z.boolean(),
    joinThreshold: z.number().int().min(2).max(500),
    joinWindowSeconds: z.number().int().min(5).max(3600),
    action: securityActionSchema,
    alertChannelId: discordIdSchema.nullable(),
    quarantineRoleId: discordIdSchema.nullable(),
    dryRun: z.boolean(),
    raidModeSeconds: z.number().int().min(0).max(86400),
    lockdownOnRaid: z.boolean(),
    minAccountAgeDuringRaidDays: z.number().int().min(0).max(365),
  }),
  antiBot: z.object({
    enabled: z.boolean(),
    minAccountAgeDays: z.number().int().min(0).max(365),
    maxFailedVerifications: z.number().int().min(1).max(50),
    action: securityActionSchema,
    dryRun: z.boolean(),
  }),
  antiAlt: z.object({
    enabled: z.boolean(),
    reviewThreshold: riskScoreSchema,
    dryRun: z.boolean(),
  }),
  antiSpam: z.object({
    enabled: z.boolean(),
    maxMessagesPerWindow: z.number().int().min(2).max(100),
    windowSeconds: z.number().int().min(1).max(120),
    maxMentionsPerMessage: z.number().int().min(1).max(50),
    blockedInvites: z.boolean(),
    blockedLinks: z.array(z.string().url().max(200)).max(50),
    escalation: z.array(securityActionSchema).max(5),
    exemptRoleIds: z.array(discordIdSchema).max(50),
    exemptChannelIds: z.array(discordIdSchema).max(50),
    dryRun: z.boolean(),
  }),
  protection: z.object({
    enabled: z.boolean(),
    alertChannelId: discordIdSchema.nullable(),
  }),
});

export const whitelistActionSchema = z.object({
  guildId: guildIdSchema,
  userId: discordIdSchema,
  reason: z.string().max(500).optional(),
});

export const apiErrorSchema = z.object({
  error: z.string(),
  code: z.string().optional(),
});
